import React, { useState } from "react";
import { motion } from "framer-motion";

const Footer = () => {
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isNewsletterLoading, setIsNewsletterLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, success) => {
    setNotification({ message, success });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    if (!newsletterEmail.trim()) {
      showNotification("Please enter a valid email address.", false);
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newsletterEmail)) {
      showNotification("Please enter a valid email address.", false);
      return;
    }

    setIsNewsletterLoading(true);

    try {
      const response = await fetch(
        "https://plm-apis.vanixtechnologies.com/api/newsletter",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: newsletterEmail }),
        },
      );

      const responseData = await response.json();
      const responseMessage =
        responseData?.message ||
        responseData?.description ||
        "Subscribed successfully";

      if (response.ok) {
        showNotification(
          responseMessage ||
            "You have successfully subscribed to our newsletter!",
          true,
        );
        setNewsletterEmail("");
      } else {
        showNotification(
          responseMessage || "Failed to subscribe. Please try again.",
          false,
        );
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      showNotification(
        error.message ||
          "Unable to subscribe. Please check your connection and try again.",
        false,
      );
    } finally {
      setIsNewsletterLoading(false);
    }
  };

  return (
    <footer
      className="w-full flex flex-col gap-10 items-center px-6 md:px-12 2xl:px-[64px] py-12 md:py-16"
      style={{ backgroundColor: "#0f141f" }}
      data-testid="footer"
    >
      {/* Notification Toast */}
      {notification && (
        <div
          className="fixed top-6 right-6 z-[9999] px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium animate-fade-in"
          style={{
            backgroundColor: notification.success ? "#00BD7E" : "#ef4444",
          }}
        >
          {notification.message}
        </div>
      )}

      {/* Main Content Holder */}
      <div
        className="w-full rounded-xl p-8 md:p-8 flex flex-col md:flex-row justify-between items-start gap-10 md:gap-8"
        style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
        data-testid="footer-content-card"
      >
        {/* Col 1 - Company */}
        <div className="flex flex-col gap-6">
          {/* MyBovine Logo */}
          <motion.div
            className="flex items-baseline gap-1"
            whileHover={{
              scale: 1.2,
            }}
            whileTap={{ scale: 0.92 }}
            style={{
              cursor: "pointer",
            }}
          >
            <motion.span
              animate={{
                textShadow: [
                  "0 0 0px rgba(42, 163, 102, 0), 0 0 10px rgba(0, 189, 126, 0.1)",
                  "0 0 12px rgba(42, 163, 102, 0.5), 0 0 24px rgba(0, 189, 126, 0.4), 0 0 40px rgba(42, 163, 102, 0.2)",
                  "0 0 0px rgba(42, 163, 102, 0), 0 0 10px rgba(0, 189, 126, 0.1)",
                ],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              style={{
                fontFamily: "Lexend, sans-serif",
                fontWeight: 700,
                fontSize: "31px",
                color: "#ffffff",
                letterSpacing: "-1.5px",
                lineHeight: "1",
              }}
            >
              MyBovine
            </motion.span>
            <motion.span
              animate={{
                textShadow: [
                  "0 0 0px rgba(0, 132, 209, 0), 0 0 12px rgba(94, 179, 246, 0.1)",
                  "0 0 14px rgba(0, 132, 209, 0.6), 0 0 28px rgba(94, 179, 246, 0.5), 0 0 44px rgba(0, 132, 209, 0.25)",
                  "0 0 0px rgba(0, 132, 209, 0), 0 0 12px rgba(94, 179, 246, 0.1)",
                ],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.15,
              }}
              style={{
                fontFamily: "Lexend, sans-serif",
                fontWeight: 300,
                fontSize: "31px",
                color: "#0084d1",
                letterSpacing: "-1.5px",
                lineHeight: "1",
              }}
            >
              .ai
            </motion.span>
          </motion.div>

          {/* Company Links */}
          <div className="flex flex-col gap-3">
            <span
              className="uppercase"
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 700,
                fontSize: "14px",
                color: "#F5FFFA",
                letterSpacing: "0.5px",
              }}
            >
              Our Company
            </span>
            <motion.a
              href="https://vanixtechnologies.com/about-us"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                display: "inline-block",
              }}
            >
              About us
            </motion.a>
            <motion.a
              href="https://vanixtechnologies.com/faqs"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                display: "inline-block",
              }}
            >
              FAQs
            </motion.a>
          </div>
        </div>

        {/* Col 2 - Newsroom */}
        <div className="flex flex-col gap-3">
          <span
            className="uppercase"
            style={{
              fontFamily: "Satoshi, sans-serif",
              fontWeight: 700,
              fontSize: "14px",
              color: "#F5FFFA",
              letterSpacing: "0.5px",
            }}
          >
            Press
          </span>
          <motion.a
            href="https://vanixtechnologies.com/press"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            style={{
              fontFamily: "Geist, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              color: "rgba(255, 255, 255, 0.6)",
              display: "inline-block",
            }}
          >
            Case studies
          </motion.a>
          <motion.a
            href="https://vanixtechnologies.com/events"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            style={{
              fontFamily: "Geist, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              color: "rgba(255, 255, 255, 0.6)",
              display: "inline-block",
            }}
          >
            Events
          </motion.a>
          <motion.a
            href="https://vanixtechnologies.com/blogs"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            style={{
              fontFamily: "Geist, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              color: "rgba(255, 255, 255, 0.6)",
              display: "inline-block",
            }}
          >
            Blogs
          </motion.a>
        </div>

        {/* Col 3 - Connect & Download */}
        <div className="flex flex-col gap-10">
          {/* Let's Connect */}
          <div className="flex flex-col gap-3">
            <span
              className="uppercase"
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 700,
                fontSize: "14px",
                color: "#F5FFFA",
                letterSpacing: "0.5px",
              }}
            >
              Let's connect
            </span>
            <div className="flex gap-4">
              {/* YouTube */}
              <a
                href="https://youtube.com/@vanixtechnologiesofficial?si=m84J-RyFofkFKa4N"
                target="_blank"
                rel="noopener noreferrer"
                style={{ opacity: 0.7, transition: "opacity 0.3s" }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="white"
                  stroke="white"
                  strokeWidth="1"
                >
                  <path d="M23 9.71a8.5 8.5 0 0 0-.91-4.13 2.92 2.92 0 0 0-1.72-1.12A69.28 69.28 0 0 0 12 4a69.28 69.28 0 0 0-8.37.46 2.92 2.92 0 0 0-1.72 1.12A8.5 8.5 0 0 0 1 9.71a57.61 57.61 0 0 0 0 4.58 8.5 8.5 0 0 0 .91 4.13 2.92 2.92 0 0 0 1.72 1.12A69.28 69.28 0 0 0 12 20a69.28 69.28 0 0 0 8.37-.46 2.92 2.92 0 0 0 1.72-1.12 8.5 8.5 0 0 0 .91-4.13 57.61 57.61 0 0 0 0-4.58zM9.74 14.85V9.15l5.15 2.85-5.15 2.85z" />
                </svg>
              </a>
              {/* Instagram */}
              <a
                href="https://www.instagram.com/vanixtechnologies?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
                target="_blank"
                rel="noopener noreferrer"
                style={{ opacity: 0.7, transition: "opacity 0.3s" }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="1.5"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="18" cy="6" r="1.5" fill="white" />
                </svg>
              </a>
              {/* LinkedIn */}
              <a
                href="https://www.linkedin.com/company/vanixtechnologies/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ opacity: 0.7, transition: "opacity 0.3s" }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Download MyBovine.ai */}
          <div className="flex flex-col gap-3">
            <span
              className="uppercase"
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 700,
                fontSize: "14px",
                color: "#F5FFFA",
                letterSpacing: "0.5px",
              }}
            >
              Download mybovine.ai
            </span>
            <div className="flex gap-4">
              {/* App Store */}
              <motion.a
                href="https://apps.apple.com/in/app/my-bovine-ai/id6758000909"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                style={{
                  display: "inline-flex",
                  height: "40px",
                  width: "120px",
                }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_cattle-intelligence/artifacts/q3qd7nug_appstorebutton.svg"
                  alt="Download on App Store"
                  className="h-[36px] sm:h-[40px]"
                />
              </motion.a>
              {/* Google Play */}
              <motion.a
                target="_blank"
                href="https://play.google.com/store/apps/details?id=com.vanix.cattle.vanix_cattle&pcampaignid=web_share"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                style={{
                  display: "inline-flex",
                  height: "40px",
                  width: "120px",
                }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_cattle-intelligence/artifacts/zffwa1x8_googleplaystorebutton.svg"
                  alt="Get it on Google Play"
                  className="h-[36px] sm:h-[40px]"
                />
              </motion.a>
            </div>
          </div>
        </div>

        {/* Col 4 - Newsletter Subscription */}
        <div className="flex flex-col gap-3 w-full md:w-[359px]">
          <span
            className="uppercase"
            style={{
              fontFamily: "Satoshi, sans-serif",
              fontWeight: 700,
              fontSize: "14px",
              color: "#F5FFFA",
              letterSpacing: "0.5px",
            }}
          >
            Subscribe to our Newsletters
          </span>
          <div className="flex flex-col gap-4 w-full">
            {/* Email Input & Subscribe Button */}
            <form
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-4"
            >
              <input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                disabled={isNewsletterLoading}
                className="flex-grow h-10 px-4 py-3 rounded disabled:opacity-50"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  fontFamily: "Geist, sans-serif",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "#FEFEFF",
                  border: "none",
                  outline: "none",
                  flex: 1,
                }}
                data-testid="newsletter-input"
              />
              <motion.button
                type="submit"
                disabled={isNewsletterLoading}
                whileHover={{ scale: isNewsletterLoading ? 1 : 1.05 }}
                whileTap={{ scale: isNewsletterLoading ? 1 : 0.95 }}
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                  fontFamily: "Geist, sans-serif",
                  fontWeight: 400,
                  fontSize: "12px",
                  color: "#ffffff",
                  padding: "12px 24px",
                  border: "none",
                  borderRadius: "4px",
                  cursor: isNewsletterLoading ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                  opacity: isNewsletterLoading ? 0.6 : 1,
                }}
                data-testid="newsletter-submit"
              >
                {isNewsletterLoading ? "Subscribing..." : "Subscribe"}
              </motion.button>
            </form>

            {/* Newsletter Description */}
            <p
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                margin: 0,
                lineHeight: "1.5",
              }}
            >
              Join the tech revolution and stay in the know. Subscribe for
              exclusive updates, offers, and a glimpse into the future
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div
        className="w-full flex flex-col md:flex-row justify-between items-center gap-6"
        data-testid="footer-bottom"
      >
        {/* Left Side - Logo & Copyright */}
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center">
          {/* Vanix Logo */}
          <img
            src="https://customer-assets.emergentagent.com/job_cattle-intelligence/artifacts/4v1y8fl1_Vanixlogowhite.svg"
            alt="Vanix Logo"
            height="32"
            className="h-[32px]"
            data-testid="footer-logo"
          />

          {/* Copyright */}
          <span
            className="text-center md:text-left"
            style={{
              fontFamily: "Satoshi, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              color: "#ffffff",
            }}
          >
            © 2026 Vanix Technologies. All rights reserved.
          </span>
        </div>

        {/* Middle - Legal Links */}
        <div className="flex gap-6">
          <motion.a
            href="https://vanixtechnologies.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            style={{
              fontFamily: "Geist, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              color: "rgba(255, 255, 255, 0.6)",
              textDecoration: "none",
              transition: "color 0.3s",
            }}
            onMouseEnter={(e) =>
              (e.target.style.color = "rgba(255, 255, 255, 1)")
            }
            onMouseLeave={(e) =>
              (e.target.style.color = "rgba(255, 255, 255, 0.6)")
            }
            data-testid="privacy-link"
          >
            Privacy Policy
          </motion.a>
          <motion.a
            href="https://vanixtechnologies.com/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            style={{
              fontFamily: "Geist, sans-serif",
              fontWeight: 400,
              fontSize: "14px",
              color: "rgba(255, 255, 255, 0.6)",
              textDecoration: "none",
              transition: "color 0.3s",
            }}
            onMouseEnter={(e) =>
              (e.target.style.color = "rgba(255, 255, 255, 1)")
            }
            onMouseLeave={(e) =>
              (e.target.style.color = "rgba(255, 255, 255, 0.6)")
            }
            data-testid="terms-link"
          >
            Terms of Services
          </motion.a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
